/**
 * Session Service - Manages automatic token refresh and session expiration handling
 * 
 * Features:
 * - Automatic token refresh on user activity (sliding 8-hour sessions)
 * - Centralized 401 handling with automatic magic link request
 * - Session expiration modal with auto-email functionality
 */

import axios from 'axios';

// Default to sending the httpOnly access_token cookie on all axios requests
// (server reads it as fallback when Authorization header is missing).
axios.defaults.withCredentials = true;

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session refresh interval: refresh token every 6 hours of activity (7-day sessions)
const REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours

// Track last activity time
let lastActivityTime = Date.now();
let refreshTimer = null;
let sessionExpiredCallback = null;

/**
 * Initialize session management
 * @param {Function} onSessionExpired - Callback when session expires
 */
export const initSessionManager = (onSessionExpired) => {
  sessionExpiredCallback = onSessionExpired;
  
  // Track user activity
  const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];
  activityEvents.forEach(event => {
    document.addEventListener(event, handleUserActivity, { passive: true });
  });
  
  // Start refresh timer
  startRefreshTimer();
  
  return () => {
    // Cleanup
    activityEvents.forEach(event => {
      document.removeEventListener(event, handleUserActivity);
    });
    if (refreshTimer) {
      clearInterval(refreshTimer);
    }
  };
};

/**
 * Handle user activity - marks that user is active
 */
const handleUserActivity = () => {
  lastActivityTime = Date.now();
};

/**
 * Start the refresh timer - checks periodically if token needs refresh
 */
const startRefreshTimer = () => {
  if (refreshTimer) {
    clearInterval(refreshTimer);
  }
  
  refreshTimer = setInterval(async () => {
    const token = localStorage.getItem('portal_token');
    if (!token) return;
    
    // Only refresh if user has been active in the last 30 minutes
    const timeSinceActivity = Date.now() - lastActivityTime;
    if (timeSinceActivity < REFRESH_INTERVAL_MS) {
      await refreshToken();
    }
  }, REFRESH_INTERVAL_MS);
};

/**
 * Refresh the current token silently
 */
export const refreshToken = async () => {
  const token = localStorage.getItem('portal_token');
  if (!token) return false;
  
  try {
    const response = await axios.post(
      `${API_URL}/api/auth/refresh`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    if (response.data.success && response.data.token) {
      // Token is now stored in httpOnly cookie by the backend.
      // We no longer write the JWT to localStorage (XSS-safe).
      return true;
    }
  } catch (error) {
    if (error.response?.status === 401) {
      return false;
    }
    // For other errors, don't log user out - might be network issue
    console.error('Token refresh failed:', error.message);
  }
  return false;
};

/**
 * Request a new magic link for session renewal
 * @param {string} email - User's email
 */
export const requestSessionRenewal = async (email) => {
  try {
    const response = await axios.post(
      `${API_URL}/api/auth/request-magic-link`,
      null,
      { params: { email } }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to request magic link:', error);
    throw error;
  }
};

/**
 * Handle 401 errors - centralized handling for expired/invalid tokens
 * @param {Error} error - Axios error
 * @returns {boolean} - True if handled (session expired modal shown)
 */
export const handle401Error = async (error) => {
  const detail = error.response?.data?.detail;
  
  if (detail === 'TOKEN_EXPIRED' || detail === 'Token has expired') {
    // Get email from stored user data
    try {
      const userData = JSON.parse(localStorage.getItem('portal_user') || '{}');
      const email = userData.email;
      
      if (sessionExpiredCallback) {
        sessionExpiredCallback(email);
        return true;
      }
    } catch (e) {
      console.error('Error getting user email:', e);
    }
  }
  
  return false;
};

/**
 * Clear all session data, invalidate server-side, and clean up
 */
export const clearSession = async () => {
  // Invalidate server-side first (best effort)
  const token = localStorage.getItem('portal_token');
  if (token) {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) {
      // Best effort - continue with client-side cleanup
    }
  }
  
  localStorage.removeItem('portal_token');
  localStorage.removeItem('portal_user');
  localStorage.removeItem('worker_token');
  localStorage.removeItem('worker_user');
  localStorage.removeItem('admin_token');
  localStorage.removeItem('client_token');
  localStorage.removeItem('user');
  
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
};

/**
 * Check if user has a valid session
 */
export const hasValidSession = () => {
  const token = localStorage.getItem('portal_token');
  return !!token;
};

export default {
  initSessionManager,
  refreshToken,
  requestSessionRenewal,
  handle401Error,
  clearSession,
  hasValidSession
};
